import {
  formatProgressStepLabel,
  type OperationProgressModel,
  type OperationProgressStep,
} from '../../../../shared/ui/progress';

export function isSameProfileApplyOperation(
  previousModel: OperationProgressModel | null,
  nextModel: OperationProgressModel | null
): boolean {
  return Boolean(
    previousModel?.operationId &&
      nextModel?.operationId &&
      previousModel.operationId === nextModel.operationId
  );
}

export function isBusyProfileApplyStatus(status: OperationProgressModel['status']): boolean {
  return status === 'pending' || status === 'running' || status === 'waiting';
}

function getStepIndexByStatus(
  steps: OperationProgressStep[] | undefined,
  statuses: OperationProgressStep['status'][]
): number {
  return steps?.findIndex((step) => statuses.includes(step.status)) ?? -1;
}

function getVisibleStepFrontierIndex(model: OperationProgressModel | null): number {
  return getVisibleStepFrontierIndexFromSteps(model?.steps);
}

function getVisibleStepFrontierIndexFromSteps(steps: OperationProgressStep[] | undefined): number {
  const safeSteps = steps ?? [];
  const activeIndex = getStepIndexByStatus(steps, ['current', 'error']);
  if (activeIndex >= 0) {
    return activeIndex;
  }

  for (let index = safeSteps.length - 1; index >= 0; index -= 1) {
    if (safeSteps[index].status === 'complete') {
      return index;
    }
  }

  return -1;
}

function findStepById(
  steps: OperationProgressStep[] | undefined,
  stepId: string
): OperationProgressStep | null {
  return steps?.find((step) => step.id === stepId) ?? null;
}

export function getActiveProfileApplyStep(
  steps: OperationProgressStep[] | undefined
): OperationProgressStep | null {
  return steps?.find((step) => step.status === 'current' || step.status === 'error') ?? null;
}

function getStabilizedStepStatus(input: {
  rawStatus: OperationProgressStep['status'];
  previousStatus: OperationProgressStep['status'] | null;
  index: number;
  visibleFrontierIndex: number;
  rawErrorIndex: number;
  previousFrontierIndex: number;
  modelStatus: OperationProgressModel['status'];
}): OperationProgressStep['status'] {
  const {
    rawStatus,
    previousStatus,
    index,
    visibleFrontierIndex,
    rawErrorIndex,
    previousFrontierIndex,
    modelStatus,
  } = input;
  const rawErrorCanOwnFrontier =
    modelStatus === 'error' &&
    rawErrorIndex >= 0 &&
    rawErrorIndex >= previousFrontierIndex;

  if (modelStatus === 'complete') {
    return rawStatus === 'skipped' ? 'skipped' : 'complete';
  }

  if (previousStatus === 'complete' && rawStatus !== 'error') {
    return 'complete';
  }

  if (index < visibleFrontierIndex) {
    return rawStatus === 'skipped' ? 'skipped' : 'complete';
  }

  if (index === visibleFrontierIndex) {
    if (rawErrorCanOwnFrontier && rawStatus === 'error') {
      return 'error';
    }

    if (isBusyProfileApplyStatus(modelStatus) && rawStatus !== 'skipped') {
      return 'current';
    }

    return previousStatus === 'current' && rawStatus === 'pending'
      ? 'current'
      : rawStatus;
  }

  if (previousStatus === 'complete' && rawStatus === 'pending') {
    return 'complete';
  }

  return rawStatus;
}

export function stabilizeProfileApplySteps(
  previousModel: OperationProgressModel | null,
  nextModel: OperationProgressModel
): OperationProgressStep[] | undefined {
  const nextSteps = nextModel.steps;
  if (!nextSteps || nextSteps.length === 0) {
    return nextSteps;
  }

  if (nextModel.status === 'error') {
    return nextSteps;
  }

  if (!isSameProfileApplyOperation(previousModel, nextModel)) {
    return normalizeCurrentProfileApplyStep(nextSteps, nextModel.status);
  }

  const previousSteps = previousModel?.steps ?? [];
  const previousFrontierIndex = getVisibleStepFrontierIndex(previousModel);
  const rawFrontierIndex = getVisibleStepFrontierIndex(nextModel);
  const visibleFrontierIndex = Math.max(previousFrontierIndex, rawFrontierIndex);
  const rawErrorIndex = getStepIndexByStatus(nextSteps, ['error']);

  return nextSteps.map((step, index) => {
    const previousStep = findStepById(previousSteps, step.id);
    return {
      ...step,
      status: getStabilizedStepStatus({
        rawStatus: step.status,
        previousStatus: previousStep?.status ?? null,
        index,
        visibleFrontierIndex,
        rawErrorIndex,
        previousFrontierIndex,
        modelStatus: nextModel.status,
      }),
    };
  });
}

function normalizeCurrentProfileApplyStep(
  steps: OperationProgressStep[],
  status: OperationProgressModel['status']
): OperationProgressStep[] {
  if (!isBusyProfileApplyStatus(status)) {
    return steps;
  }

  const currentIndex = getStepIndexByStatus(steps, ['current']);
  const visibleIndex = currentIndex >= 0
    ? currentIndex
    : Math.max(
        0,
        steps.findIndex((step) => step.status === 'pending'),
        getVisibleStepFrontierIndexFromSteps(steps)
      );

  if (visibleIndex < 0) {
    return steps;
  }

  return steps.map((step, index) =>
    index === visibleIndex
      ? { ...step, status: 'current' }
      : step.status === 'current'
        ? { ...step, status: index < visibleIndex ? 'complete' : 'pending' }
      : step
  );
}

export function buildStabilizedProfileApplyStepLabel(
  steps: OperationProgressStep[] | undefined,
  fallbackStepLabel: string | undefined
): string | undefined {
  if (!steps || steps.length === 0) {
    return fallbackStepLabel;
  }

  const activeIndex = getStepIndexByStatus(steps, ['current', 'error']);
  const frontierIndex = activeIndex >= 0
    ? activeIndex
    : Math.max(0, getVisibleStepFrontierIndexFromSteps(steps));

  return formatProgressStepLabel(frontierIndex + 1, steps.length, 1);
}
